import React, { useEffect, useRef, useState } from "react";
import { Badge, Popover, List, Typography, Button, Spin, Tag, Empty } from "antd";
import { BellOutlined, CheckOutlined } from "@ant-design/icons";
import moment from "moment";
import API from "../Utils/API";

const { Text, Title } = Typography;

const NotificationBell = ({ onNavigate, activeTab }) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const isLoadingRef = useRef(false);
  const ITEMS_PER_PAGE = 5;

  const fetchInitialNotifications = async (showLoading = true) => {
    if (isLoadingRef.current) return;
    try {
      isLoadingRef.current = true;
      if (showLoading) setLoading(true);
      const res = await API.get(`/in_app_notifications?page=1&items=${ITEMS_PER_PAGE}`);
      if (res.data) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(res.data.unread_count || 0);
        setPage(1);
        setHasMore(1 < (res.data.total_pages || 1));
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      isLoadingRef.current = false;
      if (showLoading) setLoading(false);
    }
  };

  const loadMoreNotifications = async () => {
    if (isLoadingRef.current || !hasMore) return;
    try {
      isLoadingRef.current = true;
      setLoadingMore(true);
      const nextPage = page + 1;
      const res = await API.get(`/in_app_notifications?page=${nextPage}&items=${ITEMS_PER_PAGE}`);
      if (res.data) {
        const newItems = res.data.notifications || [];
        setNotifications((prev) => [...prev, ...newItems]);
        setPage(nextPage);
        setHasMore(nextPage < (res.data.total_pages || 1));
      }
    } catch (err) {
      console.error("Error loading more notifications:", err);
    } finally {
      isLoadingRef.current = false;
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // Only call notification API automatically when on Dashboard (activeTab === "1")
    if (activeTab === "1" || !activeTab) {
      fetchInitialNotifications(false);
    }
  }, [activeTab]);

  const handleScroll = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 30 && hasMore && !isLoadingRef.current) {
      loadMoreNotifications();
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await API.patch(`/in_app_notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((item) => (item.id === id ? { ...item, read_at: new Date().toISOString() } : item))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Error marking notification as read:", err);
    }
  };

  const handleItemClick = (item) => {
    if (!item.read_at) {
      handleMarkAsRead(item.id);
    }
    setVisible(false);
    if (onNavigate && item.data) {
      onNavigate(item.data);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await API.patch("/in_app_notifications/read_all");
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, read_at: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("Error marking all notifications as read:", err);
    }
  };

  const content = (
    <div
      onScroll={handleScroll}
      style={{ width: 340, maxHeight: 350, overflowY: "auto", paddingRight: 4 }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingBottom: 10,
          borderBottom: "1px solid #f0f0f0",
          marginBottom: 10,
          position: "sticky",
          top: 0,
          backgroundColor: "#fff",
          zIndex: 1,
        }}
      >
        <Title level={5} style={{ margin: 0 }}>
          Notifications {unreadCount > 0 && <Tag color="blue">{unreadCount} New</Tag>}
        </Title>
        {unreadCount > 0 && (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={handleMarkAllAsRead}
            style={{ padding: 0 }}
          >
            Mark all read
          </Button>
        )}
      </div>

      <Spin spinning={loading}>
        {notifications.length === 0 ? (
          <Empty description="No notifications yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <>
            <List
              itemLayout="horizontal"
              dataSource={notifications}
              renderItem={(item) => {
                const isUnread = !item.read_at;
                return (
                  <List.Item
                    onClick={() => handleItemClick(item)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 6,
                      marginBottom: 6,
                      cursor: "pointer",
                      backgroundColor: isUnread ? "#e6f7ff" : "#fff",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <List.Item.Meta
                      title={
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <Text strong style={{ fontSize: 13, color: isUnread ? "#1890ff" : "#262626" }}>
                            {item.title}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {moment(item.created_at).fromNow(true)}
                          </Text>
                        </div>
                      }
                      description={
                        <div>
                          <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
                            {item.body}
                          </Text>
                          {item.actor && (
                            <Text type="secondary" style={{ fontSize: 10, fontStyle: "italic" }}>
                              By {item.actor.first_name} {item.actor.last_name}
                            </Text>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                );
              }}
            />
            {loadingMore && (
              <div style={{ textAlign: "center", padding: "8px 0" }}>
                <Spin size="small" />
              </div>
            )}
          </>
        )}
      </Spin>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      visible={visible}
      onVisibleChange={(v) => {
        setVisible(v);
        if (v) fetchInitialNotifications(true);
      }}
      placement="bottomRight"
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}>
        <Badge
          count={unreadCount}
          overflowCount={99}
          size="small"
          offset={[-2, 2]}
          style={{
            backgroundColor: "#ff4d4f",
            fontSize: 10,
            minWidth: 16,
            height: 16,
            lineHeight: "16px",
            padding: "0 4px",
          }}
        >
          <Button
            type="text"
            shape="circle"
            icon={<BellOutlined style={{ fontSize: 22, color: "#595959" }} />}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 36,
              height: 36,
              border: "none",
              boxShadow: "none",
            }}
          />
        </Badge>
      </div>
    </Popover>
  );
};

export default NotificationBell;
